/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

type PushReplaceFn = (data: Record<string, any>, title: string, url?: string) => void;
type TitleFn = (title: string) => void;

export function watchHash(window: Window, onChange: PushReplaceFn, onTitleChange: TitleFn): void {
  const {document, history, location: loc} = window;
  let currentHash = loc.hash || '#/';
  // Trigger a title change immediately to ensure that the title is up-to-date.
  onTitleChange(document.title);

  const checkHashUpdates = (): void => {
    const newHash = loc.hash;
    if (newHash !== currentHash) {
      currentHash = newHash;
      onChange({}, document.title, newHash.replace(/^#\//, '/'));
    }
  };

  const handle = (ogFn: PushReplaceFn): PushReplaceFn => (state: Record<string, any>, title: string, path?: string): void => {
    ogFn(state, title, path);
    checkHashUpdates();
  };

  history.pushState = handle(history.pushState.bind(history));
  history.replaceState = handle(history.replaceState.bind(history));

  window.onpopstate = (): void => checkHashUpdates();

  window.addEventListener('hashchange', checkHashUpdates, false);
}

/**
 * Monkey patches window.history functions in order to emit events when there
 * are changes.
 */
export function watchHistory(window: Window, onChange: PushReplaceFn, onTitleChange: TitleFn): void {
  const {document, history, location: loc} = window;
  const origin: string = loc.origin;
  // Trigger a title change immediately to ensure that the title is up-to-date.
  onTitleChange(document.title);

  const handle = (ogFn: PushReplaceFn): PushReplaceFn => (state: Record<string, any>, title: string, path?: string): void => {
    // Because we are (sometimes) overriding the base url (to make links
    // correct) the path needs to be adjusted to be correct for this domain.
    const strippedPath = (path || '').replace(/^\//, '');
    const fullPath = `${origin}/${strippedPath}`;
    ogFn(state, title, fullPath);
    onChange(state, title, `/${strippedPath}`);
  };

  history.pushState = handle(history.pushState.bind(history));
  history.replaceState = handle(history.replaceState.bind(history));
  // Because pushState from the iframe actually updates browser history,
  // we need to handle the pop state which triggers on browser navigation.
  // This allows us to alert the iframe parent about the change.
  window.onpopstate = (): void => onChange({}, '', `/${loc.pathname.replace(/^\//, '')}`);
}

/**
 * Watches window.location.href and emits events when there are changes.
 * Polls on an interval of 100ms (default).
 */
export function watchLocation(window: Window, onChange: PushReplaceFn): void {
  const {document, location: loc} = window;
  let currentLocation = '';
  let currentTitle = '';

  window.setInterval((): void => {
    if (loc.href !== currentLocation || currentTitle !== document.title) {
      currentLocation = loc.href;
      const {pathname, search, hash} = loc;
      currentTitle = document.title;
      onChange({}, currentTitle, pathname + search + hash);
    }
  }, 100);
}
